import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { OptionRight, TradingClass, TrendLine } from '../../types/entities';
import { SecurityCategory, SecuritySector } from '../../types/enums';
import { ContractDetails } from '../../types/tws-entities';

import { UserSettingsState, initialState } from './state';

const userSlice = createSlice({
  name: 'user-settings',
  initialState,
  reducers: {
    setUseFrozen: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.useFrozen = action.payload;
    },
    setShowSummary: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.showSummary = action.payload;
    },
    setUseSnapshot: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.useSnapshot = action.payload;
    },
    setNewsPanelCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.newsPanelCollapsed = action.payload;
    },
    setCashOverviewCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.cashOverviewCollapsed = action.payload;
    },
    setRegimeAnalyticsCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.regimeAnalyticsCollapsed = action.payload;
    },
    setIndicatorPanelCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.indicatorPanelCollapsed = action.payload;
    },
    setHourGraphCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.hourGraphCollapsed = action.payload;
    },
    setDailyGraphCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.dailyGraphCollapsed = action.payload;
    },
    setSelectedCategory: (state: UserSettingsState, action: PayloadAction<SecurityCategory>) => {
      state.selectedSecurityCategory = action.payload;
    },
    setSelectedContractDetails: (state: UserSettingsState, action: PayloadAction<ContractDetails | undefined>) => {
      state.selectedContractDetails = action.payload;
    },
    setSecurityPanelCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.securityPanelCollapsed = action.payload;
    },
    setSelectedSecurityId: (state: UserSettingsState, action: PayloadAction<string | undefined>) => {
      state.selectedSecurityId = action.payload;
    },
    setSelectedSecuritySector: (state: UserSettingsState, action: PayloadAction<SecuritySector | undefined>) => {
      state.selectedSecuritySector = action.payload;
    },
    setTrendLinesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.trendLinesCollapsed = action.payload;
    },
    setShortListCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.shortListCollapsed = action.payload;
    },
    setScurityPresetsCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.securityPresetsCollapsed = action.payload;
    },
    setQuickNotesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.quickNotesCollapsed = action.payload;
    },
    setLadderSetupCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.ladderSetupCollapsed = action.payload;
    },
    setLegStateCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.legStateCollapsed = action.payload;
    },
    setStrategyCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.strategyCollapsed = action.payload;
    },
    setStrategyLegsCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.strategyLegsCollapsed = action.payload;
    },
    setStrategyLogsCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.strategyLogsCollapsed = action.payload;
    },
    setBetaSummaryCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.betaSummaryCollapsed = action.payload;
    },
    setStrategyFeaturesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.strategyFeaturesCollapsed = action.payload;
    },
    setOvertakeStrategyFeaturesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.overtakeStrategyFeaturesCollapsed = action.payload;
    },
    setPursuitStrategyFeaturesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.pursuitStrategyFeaturesCollapsed = action.payload;
    },
    setChicaneStrategyFeaturesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.chicaneStrategyFeaturesCollapsed = action.payload;
    },
    setPitlaneEntryGatesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.pitlaneEntryGatesCollapsed = action.payload;
    },
    setPitlaneRiskManagementCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.pitlaneRiskManagementCollapsed = action.payload;
    },
    setRedlineCorrectionCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.redlineCorrectionCollapsed = action.payload;
    },
    setMicroHarvestCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.microHarvestCollapsed = action.payload;
    },
    setOpeningHoursCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.openingHoursCollapsed = action.payload;
    },
    setPositionTimeoutCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.positionTimeoutCollapsed = action.payload;
    },
    setStopLossCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.stopLossCollapsed = action.payload;
    },
    setDoubleUpCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.doubleUpCollapsed = action.payload;
    },
    setTrailingStopCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.trailingStopCollapsed = action.payload;
    },
    setProfitTargetCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.profitTargetCollapsed = action.payload;
    },
    setTrendEfficiencyEntryCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.trendEfficiencyEntryCollapsed = action.payload;
    },
    setDisplayPreferencesCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.displayPreferencesCollapsed = action.payload;
    },
    setRedlineRacingCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.redlineRacingCollapsed = action.payload;
    },
    setSkewEntryCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.skewEntryCollapsed = action.payload;
    },
    setZScoreEntryCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.zScoreEntryCollapsed = action.payload;
    },
    setRacingTeCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.racingTeCollapsed = action.payload;
    },
    setRaceCarCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.raceCarCollapsed = action.payload;
    },
    setEntryCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.entryCollapsed = action.payload;
    },
    setDrawdownExitCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.drawdownExitCollapsed = action.payload;
    },
    setHardExitCollapsed: (state: UserSettingsState, action: PayloadAction<boolean>) => {
      state.hardExitCollapsed = action.payload;
    },
    setSelectedContractSection: (state: UserSettingsState, action: PayloadAction<'all-contracts' | 'front-runners'>) => {
      state.selectedContractSection = action.payload;
    },
    setSelectedBackOfficeTab: (state: UserSettingsState, action: PayloadAction<'securities' | 'portfolios' | 'fundamentals'>) => {
      state.selectedBackOfficeTab = action.payload;
    },
    setSelectedDashboardTab: (state: UserSettingsState, action: PayloadAction<'active' | 'archived'>) => {
      state.selectedDashboardTab = action.payload;
    },
    setSelectedSecurityTab: (state: UserSettingsState, action: PayloadAction<string>) => {
      state.selectedSecurityTab = action.payload;
    },
    setSelectedSpreadSettingsTab: (state: UserSettingsState, action: PayloadAction<string>) => {
      state.selectedSpreadSettingsTab = action.payload;
    },
    setSelectedWatchlistTab: (state: UserSettingsState, action: PayloadAction<string>) => {
      state.selectedWatchlistTab = action.payload;
    },
    setSelectedHistoryTab: (state: UserSettingsState, action: PayloadAction<string>) => {
      state.selectedHistoryTab = action.payload;
    },
    setSelectedSector: (state: UserSettingsState, action: PayloadAction<SecuritySector>) => {
      state.selectedSector = action.payload;
    },
    setSelectedTradingClass: (
      state: UserSettingsState,
      action: PayloadAction<{ symbol: string; tradingClass: TradingClass | undefined }>
    ) => {
      state.optionSettings[action.payload.symbol] = {
        ...state.optionSettings[action.payload.symbol],
        selectedTradingClass: action.payload.tradingClass
      };
    },
    setSelectedOptionRight: (state: UserSettingsState, action: PayloadAction<{ symbol: string; right: OptionRight }>) => {
      state.optionSettings[action.payload.symbol] = {
        ...state.optionSettings[action.payload.symbol],
        selectedRight: action.payload.right
      };
    },
    setSelectedTrendLine: (state: UserSettingsState, action: PayloadAction<TrendLine | undefined>) => {
      state.selectedTrendLine = action.payload;
    },
    setHistoryYear: (state: UserSettingsState, action: PayloadAction<number>) => {
      state.historyYear = action.payload;
    },
    setHistoryMonth: (state: UserSettingsState, action: PayloadAction<number>) => {
      state.historyMonth = action.payload;
    }
  }
});

const { actions, reducer } = userSlice;
export const {
  setUseFrozen,
  setShowSummary,
  setUseSnapshot,
  setCashOverviewCollapsed,
  setRegimeAnalyticsCollapsed,
  setNewsPanelCollapsed,
  setIndicatorPanelCollapsed,
  setHourGraphCollapsed,
  setDailyGraphCollapsed,
  setSelectedCategory,
  setSelectedSecurityId,
  setSelectedContractDetails,
  setSecurityPanelCollapsed,
  setTrendLinesCollapsed,
  setShortListCollapsed,
  setScurityPresetsCollapsed,
  setQuickNotesCollapsed,
  setLadderSetupCollapsed,
  setLegStateCollapsed,
  setStrategyCollapsed,
  setStrategyLogsCollapsed,
  setStrategyLegsCollapsed,
  setBetaSummaryCollapsed,
  setStrategyFeaturesCollapsed,
  setOvertakeStrategyFeaturesCollapsed,
  setPursuitStrategyFeaturesCollapsed,
  setChicaneStrategyFeaturesCollapsed,
  setPitlaneEntryGatesCollapsed,
  setPitlaneRiskManagementCollapsed,
  setRedlineRacingCollapsed,
  setRedlineCorrectionCollapsed,
  setMicroHarvestCollapsed,
  setOpeningHoursCollapsed,
  setPositionTimeoutCollapsed,
  setStopLossCollapsed,
  setDoubleUpCollapsed,
  setTrailingStopCollapsed,
  setProfitTargetCollapsed,
  setTrendEfficiencyEntryCollapsed,
  setDisplayPreferencesCollapsed,
  setSkewEntryCollapsed,
  setZScoreEntryCollapsed,
  setRacingTeCollapsed,
  setRaceCarCollapsed,
  setEntryCollapsed,
  setDrawdownExitCollapsed,
  setHardExitCollapsed,
  setSelectedContractSection,
  setSelectedBackOfficeTab,
  setSelectedDashboardTab,
  setSelectedSecurityTab,
  setSelectedSpreadSettingsTab,
  setSelectedWatchlistTab,
  setSelectedHistoryTab,
  setSelectedTradingClass,
  setSelectedOptionRight,
  setSelectedTrendLine,
  setSelectedSector,
  setSelectedSecuritySector,
  setHistoryYear,
  setHistoryMonth
} = actions;
export default reducer;
